import { useState } from "react";
import { AlertTriangle, Loader2, Moon, Plus, RotateCcw, Save, Send, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const err = (e, d) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || d);
const RE = /^[^@\s,;]+@[^@\s,;]+\.[a-z]{2,}$/i;

function Chip({ children, onRemove, muted, testid }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border py-0.5 pl-2.5 pr-1 text-xs ${muted ? "border-amber-200 bg-amber-50 text-amber-800 line-through decoration-amber-400" : "border-slate-200 bg-white text-slate-700"}`} data-testid={testid}>
      {children}
      {onRemove && <button type="button" onClick={onRemove} className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`${testid}-remove`}><X className="h-3 w-3" /></button>}
    </span>
  );
}

/** One admin alert: its staff + extra-address recipients, saved on its own. */
export function AlertRecipientsRow({ alert, staff, canEdit, onSaved }) {
  const [ids, setIds] = useState(alert.user_ids);
  const [emails, setEmails] = useState(alert.emails);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState("");
  const k = alert.key;
  const byId = Object.fromEntries([...alert.people, ...staff].map((p) => [p.user_id, { active: true, ...p }]));
  const dirty = JSON.stringify([ids, emails]) !== JSON.stringify([alert.user_ids, alert.emails]);
  const empty = !ids.length && !emails.length;
  const addEmail = () => {
    const e = draft.trim().toLowerCase();
    if (!RE.test(e)) { toast.error("Enter a full e-mail address"); return; }
    if (!emails.includes(e)) setEmails([...emails, e]);
    setDraft("");
  };
  const save = async () => {
    setBusy("save");
    try { await api.put(`/admin-alerts/${k}`, { user_ids: ids, emails }); toast.success(`${alert.label}: recipients saved`); onSaved(); }
    catch (e) { err(e, "Could not save"); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const { data } = await api.post(`/admin-alerts/${k}/test`); toast.success(data.status === "paused" ? "Test held in the outbox (sending is paused)" : `Test sent to ${data.to.length}`); }
    catch (e) { err(e, "Could not send the test"); } finally { setBusy(""); }
  };
  const hold = async (v) => {
    setBusy("hold");
    try { await api.put(`/admin-alerts/${k}`, { hold: v }); toast.success(v ? `${alert.label}: held during quiet hours` : `${alert.label}: always sent straight away`); onSaved(); }
    catch (e) { err(e, "Could not change it"); } finally { setBusy(""); }
  };
  const options = staff.filter((s) => !ids.includes(s.user_id));
  return (
    <div className="space-y-2 border-t border-slate-100 px-5 py-4 first:border-t-0" data-testid={`admin-alert-${k}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-800">{alert.label}</div>
          <p className="text-xs text-slate-500">{alert.sends_when}</p>
        </div>
        <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-slate-600" title="Wait for the morning e-mail during quiet hours">
          <Moon className={`h-3.5 w-3.5 ${alert.hold ? "text-indigo-500" : "text-slate-300"}`} /> Hold overnight
          <Switch checked={alert.hold} disabled={!canEdit || !!busy} onCheckedChange={hold} data-testid={`admin-alert-hold-${k}`} /></label>
        {alert.held > 0 && <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-800" data-testid={`admin-alert-held-${k}`}>{alert.held} held</span>}
        {canEdit && <Button size="sm" variant="outline" className="h-8" disabled={empty || dirty || !!busy} onClick={test} title={dirty ? "Save first" : undefined} data-testid={`admin-alert-test-${k}`}>
          {busy === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test</Button>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-testid={`admin-alert-chips-${k}`}>
        {empty && <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700" data-testid={`admin-alert-empty-${k}`}><AlertTriangle className="h-3.5 w-3.5" /> Nobody — this alert is not sent</span>}
        {ids.map((id) => { const p = byId[id] || { name: "Removed account", active: false };
          return <Chip key={id} muted={!p.active} testid={`admin-alert-person-${k}-${id}`} onRemove={canEdit ? () => setIds(ids.filter((x) => x !== id)) : null}>
            {p.name}{p.email && <span className="text-slate-400">{p.email}</span>}{!p.active && <span className="no-underline"> · deactivated, not sent</span>}</Chip>; })}
        {emails.map((e) => <Chip key={e} testid={`admin-alert-email-${k}-${e}`} onRemove={canEdit ? () => setEmails(emails.filter((x) => x !== e)) : null}>{e}</Chip>)}
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Select value="" onValueChange={(v) => setIds([...ids, v])}>
            <SelectTrigger className="h-8 w-56 text-xs" data-testid={`admin-alert-pick-${k}`}><SelectValue placeholder="Add a staff member…" /></SelectTrigger>
            <SelectContent>{options.map((s) => <SelectItem key={s.user_id} value={s.user_id} data-testid={`admin-alert-option-${k}-${s.user_id}`}>{s.name} · {s.email}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                 placeholder="or any e-mail address" className="h-8 w-64 text-xs" data-testid={`admin-alert-email-input-${k}`} />
          <Button size="sm" variant="ghost" className="h-8" disabled={!draft.trim()} onClick={addEmail} data-testid={`admin-alert-email-add-${k}`}><Plus className="h-3.5 w-3.5" /> Add</Button>
          {dirty && <>
            <Button size="sm" className="h-8" disabled={!!busy} onClick={save} data-testid={`admin-alert-save-${k}`}>{busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => { setIds(alert.user_ids); setEmails(alert.emails); }} data-testid={`admin-alert-undo-${k}`}><RotateCcw className="h-3.5 w-3.5" /> Undo</Button>
          </>}
          {alert.updated_by && !dirty && <span className="text-[11px] text-slate-400" data-testid={`admin-alert-changed-${k}`}>Last changed by {alert.updated_by}{alert.updated_at ? ` · ${new Date(alert.updated_at).toLocaleString()}` : ""}</span>}
        </div>)}
    </div>
  );
}
